<template>
  <div>
    <vs-alert
      :active="!this.trustedPartner && !this.loading"
      color="danger"
      icon="error_outline"
    >
      <span>Trusted partner ID: {{ trustedPartnerId }} not found!</span>
    </vs-alert>
    <vs-progress v-if="loading" indeterminate color="primary"></vs-progress>
    <div v-if="trustedPartner && !loading">
      <div id="main-container">
        <vx-card class="mb-5">
          <vs-row vs-justify="space-between" vs-align="center">
            <div>
              <h2>{{ trustedPartner.name }} (ID: {{ trustedPartner.id }})</h2>
            </div>
            <div>
              <vs-button color="primary" @click="save" :disabled="loading">Save</vs-button>
            </div>
          </vs-row>
        </vx-card>

        <vs-alert
          :active="!!errors.length"
          color="danger"
          icon="error_outline"
          class="mb-5"
        >
          <div v-for="(error, index) in errors" :key="`tp_error_${index}`">
            {{ error }}
          </div>
        </vs-alert>

        <div class="vx-row">
          <div class="vx-col lg:w-1/2 w-full">
            <vx-card title="General" class="mb-5">
              <div class="vx-row mb-4">
                <div class="vx-col lg:w-1/2 w-full mb-3">
                  <vs-input
                    class="w-full"
                    label="Name"
                    v-model="workingTrustedPartner.name"
                  />
                </div>
                <div class="vx-col lg:w-1/2 w-full">
                  <vs-input
                    class="w-full"
                    label="Web Url"
                    v-model="workingTrustedPartner.web_url"
                  />
                </div>
              </div>
              <div class="vx-row">
                <div class="vx-col w-full">
                  <label class="label" for="active-switch">Active</label>
                  <vs-switch
                    id="active-switch"
                    color="success"
                    v-bind:checked="workingTrustedPartner.active"
                    v-on:input="workingTrustedPartner.active = !workingTrustedPartner.active"/>
                </div>
              </div>
            </vx-card>
            <div class="vx-row">
              <div class="vx-col w-full mb-5">
                <vx-card title="Image">
                  <itovi-image-upload
                    :initial-image-url="getImageUrl()"
                    :identifier="'primary'"
                    :required-aspect-ratio="1"
                    label-top="PNG 1:1 aspect ratio required (i.e. 500x500)"
                    button-text="Add Image"
                    @imageSelected="(image) => this.newImage = image"
                  />
                </vx-card>
              </div>
            </div>
            <div class="vx-row">
              <div class="vx-col w-full mb-5">
                <trusted-partner-companies
                  :companies.sync="trustedPartner.companies"
                  :trustedPartnerId="trustedPartner.id"
                />
              </div>
            </div>
          </div>
          <div class="vx-col lg:w-1/2 w-full mb-5">
            <vx-card title="Descriptions">
              <template v-for="(language, index) in languages" :index="index">
                <vs-textarea
                  class="w-full py-2 my-2"
                  :key="`language-${language.language_id}`"
                  :label="language.name"
                  maxlength="255"
                  v-model="workingLocalizations[index].description"
                />
              </template>
            </vx-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/http/api";
import ItoviImageUpload from "@/layouts/components/ItoviImageUpload";
import {productHelpers} from '@/models/helpers';
import TrustedPartnerCompanies from "@/views/content/trusted-partners/TrustedPartnerCompanies";

export default {
  components: {
    ItoviImageUpload,
    TrustedPartnerCompanies
  },
  data() {
    return {
      loading: false,
      trustedPartner: null,
      trustedPartnerId: parseInt(this.$route.params.id),
      languages: null,
      workingTrustedPartner: null,
      workingLocalizations: null,
      newImage: null,
      helpers: productHelpers,
      errors: []
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;

      const [trustedPartner, languages] = await Promise.all([
        api.getTrustedPartner(this.trustedPartnerId),
        api.fetchAllLanguages()
      ]);

      this.trustedPartner = trustedPartner;
      this.languages = languages;

      this.buildWorkingLocalizations();
      this.buildWorkingTrustedPartner();

      this.loading = false;
    },
    buildWorkingTrustedPartner() {
      this.workingTrustedPartner = {
        id: this.trustedPartner.id || 0,
        name: this.trustedPartner.name || '',
        web_url: this.trustedPartner.web_url || '',
        active: this.trustedPartner.active || 0
      }
    },
    buildWorkingLocalizations() {
      this.workingLocalizations = this.languages.map((language) => {
        const matchedLanguage = this.trustedPartner.localizations.filter((localization) => localization.language_id === language.language_id)[0];
        const description = matchedLanguage ? matchedLanguage.description : '';

        return {
          trusted_partner_id: this.trustedPartnerId,
          language_id: language.language_id,
          description: description
        }
      });
    },
    async save() {
      this.errors = [];
      this.loading = true;

      await this.updateGeneral();
      await this.updateLocalizations();
      await this.updateImage();
      await this.fetchData();

      if (!this.errors.length) {
        this.$vs.notify({
          title: 'Success',
          text:  'Saved Successfully!',
          color: 'success',
          icon:  'check_circle_outline'
        })
      }

      this.loading = false;
    },
    async updateGeneral() {
      const result = await api.updateTrustedPartner(this.workingTrustedPartner);
      if (!result) this.errors.push('The General section was NOT saved successfully.');
    },
    async updateLocalizations() {
      const result = await api.updateTrustedPartnerLocalizations(this.workingLocalizations);
      if (!result) this.errors.push('The Localizations section was NOT saved successfully.');
    },
    async updateImage() {
      if (!this.newImage) return;
      const result = await api.updateTrustedPartnerImage({image: this.newImage, trusted_partner_id: this.trustedPartnerId});
      if (!result) this.errors.push('There was an error saving the image.');
    },
    getImageUrl() {
      const image = this.trustedPartner.images.filter((image) => image.image_type.name === 'trusted_partner' && image.image_size.name === 'regular')[0];
      return image ? image.url : 'https://www.revereelectric.com/ASSETS/WEB_THEMES//REVERE_ELECTRIC/images/NoImage.png';
    }
  }
}
</script>
